import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Users, CreditCard, BarChart3, ListChecks, Brain, ArrowLeft, IndianRupee,
  Trash2, Edit2, Plus, Upload, Download
} from "lucide-react";
import api, { API } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import QuestionEditor from "../components/QuestionEditor";
import LanguageSwitcher from "../components/LanguageSwitcher";

export default function Admin() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const TABS = [
    { id: "analytics", label: t("admin.tab_analytics"), icon: BarChart3 },
    { id: "users", label: t("admin.tab_users"), icon: Users },
    { id: "payments", label: t("admin.tab_payments"), icon: CreditCard },
    { id: "questions", label: t("admin.tab_questions"), icon: ListChecks },
  ];

  const [tab, setTab] = useState("analytics");
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [replaceOnImport, setReplaceOnImport] = useState(false);
  const [filterSection, setFilterSection] = useState("all");
  const [filterDifficulty, setFilterDifficulty] = useState("all");

  useEffect(() => {
    if (!user) { navigate("/auth"); return; }
    if (user.role !== "admin") { navigate("/dashboard"); return; }
  }, [user, navigate]);

  const load = async () => {
    try {
      if (tab === "analytics") {
        const r = await api.get("/admin/analytics"); setAnalytics(r.data);
      } else if (tab === "users") {
        const r = await api.get("/admin/users"); setUsers(r.data);
      } else if (tab === "payments") {
        const r = await api.get("/admin/payments"); setPayments(r.data);
      } else if (tab === "questions") {
        const r = await api.get("/questions"); setQuestions(r.data);
      }
    } catch (e) {
      toast.error("Failed to load");
    }
  };

  useEffect(() => { if (user?.role === "admin") load(); /* eslint-disable-next-line */ }, [tab, user]);

  const deleteQ = async (qid) => {
    if (!window.confirm(t("admin.confirm_delete"))) return;
    try {
      await api.delete(`/admin/questions/${qid}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  const exportCSV = async () => {
    try {
      const token = localStorage.getItem("bharatiq_token");
      const r = await fetch(`${API}/admin/questions/export-csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error("Export failed");
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "bharatiq-questions.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(e.message || "Export failed");
    }
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const token = localStorage.getItem("bharatiq_token");
      const r = await fetch(`${API}/admin/questions/import-csv?replace=${replaceOnImport}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Import failed");
      toast.success(t("admin.imported_msg", { n: data.imported }));
      if (data.errors?.length) {
        toast.error(`${data.errors.length} rows had errors`);
      }
      e.target.value = "";
      load();
    } catch (err) {
      toast.error(err.message || "Import failed");
    }
  };

  if (!user || user.role !== "admin") return null;

  const filteredQs = questions.filter((q) => {
    if (filterSection !== "all" && q.section !== filterSection) return false;
    if (filterDifficulty !== "all" && q.difficulty !== filterDifficulty) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-md bg-primary text-primary-foreground grid place-items-center">
              <Brain className="w-4 h-4" />
            </div>
            <span className="font-display font-extrabold">BharatIQ</span>
            <span className="ml-1 text-xs uppercase tracking-widest px-2 py-0.5 rounded bg-primary/10 text-primary font-bold">Admin</span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/dashboard" className="text-sm text-muted-foreground inline-flex items-center gap-1.5 hover:text-primary"><ArrowLeft className="w-4 h-4" /> {t("admin.back")}</Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid lg:grid-cols-[220px_1fr] gap-8">
        <aside className="space-y-1">
          {TABS.map((tt) => (
            <button
              key={tt.id}
              data-testid={`admin-tab-${tt.id}`}
              onClick={() => setTab(tt.id)}
              className={`w-full text-left flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                tab === tt.id ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:bg-muted"
              }`}
            >
              <tt.icon className="w-4 h-4" /> {tt.label}
            </button>
          ))}
        </aside>

        <main>
          {tab === "analytics" && analytics && (
            <div data-testid="admin-analytics">
              <h2 className="font-display text-2xl font-extrabold tracking-tight">{t("admin.analytics_title")}</h2>
              <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label="Total Users" value={analytics.total_users} />
                <StatCard label="Sessions" value={analytics.total_sessions} sub={`${analytics.completed_sessions} completed`} />
                <StatCard label="Paid (Basic)" value={analytics.paid_basic} />
                <StatCard label="Paid (Premium)" value={analytics.paid_premium} />
                <StatCard label="Revenue" value={`₹${analytics.total_revenue_inr.toLocaleString("en-IN")}`} icon={IndianRupee} />
                <StatCard label="Conversion" value={`${analytics.conversion_rate_pct}%`} sub="paid / completed" />
              </div>
            </div>
          )}

          {tab === "users" && (
            <div data-testid="admin-users">
              <h2 className="font-display text-2xl font-extrabold tracking-tight">{t("admin.users_title")}</h2>
              <div className="mt-5 rounded-xl border border-border bg-white overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left text-xs uppercase tracking-widest text-muted-foreground">
                    <tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Joined</th></tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-t border-border">
                        <td className="p-3 font-medium">{u.name}</td>
                        <td className="p-3 text-muted-foreground">{u.email}</td>
                        <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded ${u.role === "admin" ? "bg-accent/15 text-accent" : "bg-muted text-foreground/70"}`}>{u.role}</span></td>
                        <td className="p-3 text-muted-foreground text-xs">{new Date(u.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === "payments" && (
            <div data-testid="admin-payments">
              <h2 className="font-display text-2xl font-extrabold tracking-tight">{t("admin.payments_title")}</h2>
              <div className="mt-5 rounded-xl border border-border bg-white overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left text-xs uppercase tracking-widest text-muted-foreground">
                    <tr><th className="p-3">Order ID</th><th className="p-3">Tier</th><th className="p-3">Amount</th><th className="p-3">Status</th><th className="p-3">Date</th></tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.id} className="border-t border-border">
                        <td className="p-3 font-mono text-xs">{p.provider_order_id}</td>
                        <td className="p-3">{p.tier}</td>
                        <td className="p-3 font-medium">₹{p.amount}</td>
                        <td className="p-3">
                          <span className={`text-xs px-2 py-0.5 rounded ${p.status === "success" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>{p.status}</span>
                        </td>
                        <td className="p-3 text-muted-foreground text-xs">{new Date(p.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === "questions" && (
            <div data-testid="admin-questions">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="font-display text-2xl font-extrabold tracking-tight">{t("admin.questions_title")}</h2>
                  <div className="mt-1 text-sm text-muted-foreground">
                    {t("admin.questions_count", { n: questions.length })}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <input ref={fileInputRef} type="file" accept=".csv" onChange={onImportFile} className="hidden" data-testid="admin-csv-input" />
                  <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                    <input type="checkbox" checked={replaceOnImport} onChange={(e) => setReplaceOnImport(e.target.checked)} data-testid="admin-import-replace" />
                    {t("admin.import_replace")}
                  </label>
                  <button onClick={() => fileInputRef.current?.click()} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium border border-border bg-white hover:bg-muted" data-testid="admin-import-btn">
                    <Upload className="w-4 h-4" /> {t("admin.import_csv")}
                  </button>
                  <button onClick={exportCSV} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium border border-border bg-white hover:bg-muted" data-testid="admin-export-btn">
                    <Download className="w-4 h-4" /> {t("admin.export_csv")}
                  </button>
                  <button onClick={() => { setEditing(null); setEditorOpen(true); }} className="btn-primary inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold" data-testid="admin-add-q-btn">
                    <Plus className="w-4 h-4" /> {t("admin.add_question")}
                  </button>
                </div>
              </div>

              {/* Filters */}
              <div className="mt-5 flex flex-wrap gap-2">
                <select value={filterSection} onChange={(e) => setFilterSection(e.target.value)} className="text-xs px-3 py-1.5 rounded-md border border-border bg-white" data-testid="admin-filter-section">
                  <option value="all">All sections</option>
                  <option value="critical">Critical</option>
                  <option value="personality">Personality</option>
                  <option value="eq">EQ</option>
                </select>
                <select value={filterDifficulty} onChange={(e) => setFilterDifficulty(e.target.value)} className="text-xs px-3 py-1.5 rounded-md border border-border bg-white" data-testid="admin-filter-difficulty">
                  <option value="all">All difficulty</option>
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                </select>
              </div>

              <div className="mt-4 space-y-3">
                {filteredQs.map((q) => (
                  <div key={q.id} className="rounded-lg border border-border bg-white p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-primary/5 text-primary font-bold">{q.section}</span>
                          <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded font-bold ${
                            q.difficulty === "hard" ? "bg-destructive/10 text-destructive" :
                            q.difficulty === "easy" ? "bg-primary/10 text-primary" : "bg-accent/15 text-accent"
                          }`}>{q.difficulty || "medium"}</span>
                          {q.category && <span className="text-[10px] text-muted-foreground">{q.category}</span>}
                          <span className="text-xs text-muted-foreground">#{q.order}</span>
                        </div>
                        <div className="mt-2 font-medium truncate">{q.text}</div>
                        {q.text_hi && <div className="mt-0.5 text-xs text-muted-foreground truncate">{q.text_hi}</div>}
                        <div className="mt-2 text-xs text-muted-foreground">
                          {q.options.length} options{q.trait ? ` · trait ${q.trait}` : ""}{q.reverse ? " · reverse" : ""}
                          {q.correct_index != null ? ` · correct: ${String.fromCharCode(65 + q.correct_index)}` : ""}
                        </div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <button onClick={() => { setEditing(q); setEditorOpen(true); }} className="p-2 text-muted-foreground hover:text-primary transition-colors" data-testid={`admin-q-edit-${q.id}`}>
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => deleteQ(q.id)} className="p-2 text-muted-foreground hover:text-destructive transition-colors" data-testid={`admin-q-delete-${q.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {filteredQs.length === 0 && (
                  <div className="text-center py-12 text-sm text-muted-foreground">No questions match the filters.</div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>

      <QuestionEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        question={editing}
        onSaved={load}
      />
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon }) {
  return (
    <div className="rounded-xl border border-border bg-white p-5">
      <div className="flex items-center justify-between">
        <div className="label-eyebrow">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-muted-foreground" />}
      </div>
      <div className="mt-2 font-display text-3xl font-extrabold text-primary">{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}
